import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import ScrollController from "./components/ScrollToTop";
import SplashScreen from "./components/SplashScreen";
import { AuthProvider } from "./contexts/auth/auth-provider";
import { GuestGuard } from "./contexts/auth/guest-guard";
import { CookieConsent } from "./components/CookieConsent";
import { useAnalytics } from "./hooks/useAnalytics";
import { useAuthContext } from "./hooks/use-auth-context";

const ProductLandingPage = lazy(() => import("./pages/public/ProductLandingPage"));
const CreatorProfile = lazy(() => import("./pages/public/CreatorProfile"));
const ReferralLanding = lazy(() => import("./pages/public/ReferralLanding"));
const FeeCalculator = lazy(() => import("./pages/public/FeeCalculator"));
const Marketplace = lazy(() => import("./pages/public/Marketplace"));
const Privacy = lazy(() => import("./pages/public/Privacy"));
const Terms = lazy(() => import("./pages/public/Terms"));
const Cookies = lazy(() => import("./pages/public/Cookies"));
const Help = lazy(() => import("./pages/public/Help"));
const Pricing = lazy(() => import("./pages/public/Pricing"));
const CompareGumroad = lazy(() => import("./pages/public/CompareGumroad"));
const CompareTeachable = lazy(() => import("./pages/public/CompareTeachable"));
const ComparePodia = lazy(() => import("./pages/public/ComparePodia"));
const Blog = lazy(() => import("./pages/public/Blog"));
const BlogPost = lazy(() => import("./pages/public/BlogPost"));
const MarketingCommandCenter = lazy(() => import("./pages/admin/MarketingCommandCenter"));
const ConnectedSites = lazy(() => import("./pages/imarketer/ConnectedSites"));
const NewConnectedSite = lazy(() => import("./pages/imarketer/NewConnectedSite"));
const SignUp = lazy(() => import("./pages/auth/SignUp"));
const Login = lazy(() => import("./pages/auth/Login"));
const Verify = lazy(() => import("./pages/auth/Verify"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const PostAuth = lazy(() => import("./pages/auth/PostAuth"));
const Mfa = lazy(() => import("./pages/auth/Mfa"));
const ProductAccess = lazy(() => import("./pages/auth/ProductAccess"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = (globalThis as any).__qc ?? new QueryClient();
(globalThis as any).__qc = queryClient;

const PageViewTracker = () => {
  const location = useLocation();
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.trackPageView(location.pathname);
  }, [location.pathname, analytics]);

  return null;
};

const UserIdentifier = () => {
  const { user } = useAuthContext();
  const analytics = useAnalytics();

  useEffect(() => {
    if (user?.id && analytics.isEnabled) {
      analytics.identify(user.id, {
        role: user.role,
      });
    }
  }, [user?.id, user?.role, analytics]);

  return null;
};

const AppContent = () => (
  <>
    <ScrollController />
    <PageViewTracker />
    <UserIdentifier />
    <Suspense fallback={<SplashScreen />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/ref/:code" element={<ReferralLanding />} />
        <Route path="/fee-calculator" element={<FeeCalculator />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/help" element={<Help />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/compare/gumroad" element={<CompareGumroad />} />
        <Route path="/compare/teachable" element={<CompareTeachable />} />
        <Route path="/compare/podia" element={<ComparePodia />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/admin/marketing" element={<MarketingCommandCenter requireLintAdmin />} />
        <Route path="/imarketer" element={<Navigate to="/imarketer/sites/lint-demo/analytics" replace />} />
        <Route path="/imarketer/sites" element={<ConnectedSites />} />
        <Route path="/imarketer/sites/new" element={<NewConnectedSite />} />
        <Route path="/imarketer/sites/:siteId/analytics" element={<MarketingCommandCenter />} />
        <Route path="/imarketer/sites/:siteId/recommendations" element={<MarketingCommandCenter />} />

        <Route
          path="/auth/signup"
          element={
            <GuestGuard>
              <SignUp />
            </GuestGuard>
          }
        />
        <Route
          path="/auth/login"
          element={
            <GuestGuard>
              <Login />
            </GuestGuard>
          }
        />
        <Route
          path="/auth/forgot-password"
          element={
            <GuestGuard>
              <ForgotPassword />
            </GuestGuard>
          }
        />

        <Route path="/auth/verify" element={<Verify />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/auth/post-auth" element={<PostAuth />} />
        <Route path="/auth/mfa" element={<Mfa />} />
        <Route path="/auth/access" element={<ProductAccess />} />

        <Route path="/p/:productId" element={<ProductLandingPage />} />
        <Route path="/creator/:id" element={<CreatorProfile />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </BrowserRouter>
          <CookieConsent />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
